import { TimeZone } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { omit } from 'lodash-es';
import { useCallback, useMemo } from 'react';

import useCompanyQuery from '~queries/useCompanyQuery';
import useCsvImportLookupsQuery, {
  CsvImportLookupErrors,
  CsvImportLookupName,
  CsvImportLookups,
} from '~queries/useCsvImportLookupsQuery';

import { createCustomersConfig } from './configs/customersConfig';
import { createDriversConfig } from './configs/driversConfig';
import { createEquipmentConfig } from './configs/equipmentConfig';
import { createMaterialsConfig } from './configs/materialsConfig';
import { createSitesConfig } from './configs/sitesConfig';
import { EntityCSVConfig } from './configs/types';
import { createUsersConfig } from './configs/usersConfig';
import { createVendorsConfig } from './configs/vendorsConfig';

const toNameKey = (name: string) => name.trim().toLowerCase();

// A shared name is ambiguous; failing the row beats attaching the wrong record.
export const createNameResolver = (
  items: { id: string; name: string }[],
  loadError?: string,
) => {
  const idsByName = new Map<string, string[]>();

  items.forEach(({ id, name }) => {
    const key = toNameKey(name);
    idsByName.set(key, [...(idsByName.get(key) ?? []), id]);
  });

  return (name: string) => {
    if (loadError) {
      throw new Error(loadError);
    }

    const ids = idsByName.get(toNameKey(name));

    return ids?.length === 1 ? ids[0] : undefined;
  };
};

export const requireResolvedId =
  (resolveId: (name: string) => string | undefined, column: string) => (name: string) => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return undefined;
    }

    const id = resolveId(trimmedName);
    if (!id) {
      throw new Error(
        t('csv_import_export.errors.invalid_reference', {
          field: column,
          value: trimmedName,
        }),
      );
    }

    return id;
  };

const buildResolvers = (
  {
    connectedAccounts,
    departments,
    equipmentTypes,
    materialTypes,
    roles,
  }: CsvImportLookups,
  lookupErrors: CsvImportLookupErrors = {},
) => {
  const connectedCompanies = connectedAccounts.flatMap(({ connectedCompanyId, name }) =>
    connectedCompanyId ? [{ id: connectedCompanyId, name }] : [],
  );
  const connectedCompanyNamesById = new Map(
    connectedCompanies.map(({ id, name }) => [id, name]),
  );

  return {
    getConnectedCompanyIdByName: createNameResolver(
      connectedCompanies,
      lookupErrors.connectedAccounts,
    ),
    getConnectedCompanyNameById: (id: string) => connectedCompanyNamesById.get(id),
    getEquipmentTypeIdByName: createNameResolver(
      equipmentTypes,
      lookupErrors.equipmentTypes,
    ),
    getRoleIdByName: createNameResolver(roles, lookupErrors.roles),
    requireDepartmentId: requireResolvedId(
      createNameResolver(departments, lookupErrors.departments),
      'department',
    ),
    requireMaterialTypeId: requireResolvedId(
      createNameResolver(materialTypes, lookupErrors.materialTypes),
      'material_type',
    ),
    roleNames: roles.map((role) => role.name.toUpperCase()),
  };
};

interface EntityCsvConfigEntry {
  createConfig: (
    resolvers: ReturnType<typeof buildResolvers>,
    companyTimeZone: TimeZone | null,
    companyId: string,
  ) => EntityCSVConfig<unknown>;
  // The users role column lists the company's roles before a file is picked.
  isLoadedEagerly?: boolean;
  lookupNames: CsvImportLookupName[];
  usesCompanyTimeZone?: boolean;
}

const ENTITY_CSV_CONFIGS = {
  customers: {
    createConfig: ({ requireDepartmentId }) => createCustomersConfig(requireDepartmentId),
    lookupNames: ['departments'],
  },
  drivers: {
    createConfig: ({ getRoleIdByName }) => createDriversConfig(getRoleIdByName),
    lookupNames: ['roles'],
  },
  equipment: {
    createConfig: (resolvers, _companyTimeZone, companyId) =>
      createEquipmentConfig(resolvers, companyId),
    lookupNames: ['connectedAccounts', 'equipmentTypes'],
  },
  materials: {
    createConfig: ({ requireMaterialTypeId }, _companyTimeZone, companyId) =>
      createMaterialsConfig(requireMaterialTypeId, companyId),
    lookupNames: ['materialTypes'],
  },
  sites: {
    createConfig: ({ requireDepartmentId }, companyTimeZone, companyId) =>
      createSitesConfig(requireDepartmentId, companyTimeZone, companyId),
    lookupNames: ['departments'],
    usesCompanyTimeZone: true,
  },
  users: {
    createConfig: ({ getRoleIdByName, roleNames }) =>
      createUsersConfig(getRoleIdByName, roleNames),
    isLoadedEagerly: true,
    lookupNames: ['roles'],
  },
  vendors: {
    createConfig: ({ requireDepartmentId }) => createVendorsConfig(requireDepartmentId),
    lookupNames: ['departments'],
  },
} satisfies Record<string, EntityCsvConfigEntry>;

export type EntityCsvConfigKey = keyof typeof ENTITY_CSV_CONFIGS;

const NO_LOADED_LOOKUPS: Partial<CsvImportLookups> = {};

export default function useEntityCsvConfig(
  entityKey: EntityCsvConfigKey,
  companyId: string,
  loadedLookups = NO_LOADED_LOOKUPS,
) {
  const entry: EntityCsvConfigEntry = ENTITY_CSV_CONFIGS[entityKey];
  const lookupNames = useMemo(
    () => entry.lookupNames.filter((name) => !loadedLookups[name]?.length),
    [entry, loadedLookups],
  );
  const { loadLookups, lookups } = useCsvImportLookupsQuery({
    companyId,
    isEnabled: Boolean(entry.isLoadedEagerly),
    lookupNames,
  });
  const { company } = useCompanyQuery({
    companyId: entry.usesCompanyTimeZone ? companyId : undefined,
  });
  const companyTimeZone = company?.timeZone ?? null;

  const createConfig = useCallback(
    (fetchedLookups: CsvImportLookups, lookupErrors?: CsvImportLookupErrors) =>
      entry.createConfig(
        buildResolvers(
          { ...fetchedLookups, ...omit(loadedLookups, lookupNames) },
          lookupErrors,
        ),
        companyTimeZone,
        companyId,
      ),
    [companyId, companyTimeZone, entry, loadedLookups, lookupNames],
  );

  const config = useMemo(() => createConfig(lookups), [createConfig, lookups]);

  const loadConfig = useCallback(async () => {
    const { errors, lookups: freshLookups } = await loadLookups();

    return createConfig(freshLookups, errors);
  }, [createConfig, loadLookups]);

  return { config, loadConfig };
}
